import { bootstrapApplication } from "@angular/platform-browser";
import { provideAnimations } from "@angular/platform-browser/animations";
import { APP_INITIALIZER, ErrorHandler, importProvidersFrom } from "@angular/core";
import {PreloadAllModules, provideRouter, withPreloading} from "@angular/router";
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from "@angular/common/http"; // Use provideHttpClient
import { AppComponent } from "./app/app.component"; // Assuming path to your AppComponent
// --- Library Modules with Providers ---
import { ToastrModule } from "ngx-toastr";
import { QuillModule } from "ngx-quill";
import { NgxEchartsModule } from "ngx-echarts";
import { NgxSkeletonLoaderModule } from "ngx-skeleton-loader";

// --- Providers/Interceptors/Configuration ---
import { AuthInterceptor } from "./app/services/auth.interceptor"; // Adjust path if needed
import { DEFAULT_TIMEOUT, TimeoutInterceptor } from "./app/services/timeout.interceptor"; // Adjust path if needed
import { CoreInitializationService } from "./app/services/core-initialization.service";
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from "@angular/material/form-field";
import { visibilityChangeListenerFactory } from "./app/visibility-change-listener.factory"; // Adjust path if needed
import { ViewTaskSelectionHandlingService } from "./app/modules/shared/Services/view-task-selection-handling.service"; // Adjust path if needed
import { GlobalErrorHandler } from "./app/UtilFunctions/gloabErrorHandler"; // Adjust path if needed
import { SharedModule } from "./app/modules/shared/shared.module"; // Adjust path if needed
// --- Firebase ---
import { initializeApp } from "firebase/app";
import { environment } from "./environments/environment";

// --- Echarts ---
import * as echarts from "echarts/core";
import { BarChart, LineChart, PieChart } from "echarts/charts";
import {
    DataZoomComponent,
    GraphicComponent,
    GridComponent,
    LegendComponent,
    MarkLineComponent,
    TitleComponent,
    TooltipComponent,
} from "echarts/components";
import { CanvasRenderer } from "echarts/renderers";
import { MatDialogModule } from "@angular/material/dialog";
import { appRoutes } from "./app/app.routes";
import "./app/UtilFunctions/customutils";
import {provideNativeDateAdapter} from "@angular/material/core";
import {provideCustomDateAdapters} from "./app/datepicker.providers";
import {provideNgxMatDateAdapter} from "@naseebsingh3510/datetime-picker";
import {CUSTOM_DATE_TIME_FORMATS} from "./app/models/constants";
import {CustomAdapter} from "./app/custom-date-adapter";
import {setupConsoleInterceptor} from "./app/UtilFunctions/console.factory";

// --- Initialize Firebase ---
initializeApp(environment.firebase);

// --- Initialize Echarts ---
echarts.use([
    TitleComponent,
    TooltipComponent,
    GridComponent,
    BarChart,
    LegendComponent,
    CanvasRenderer,
    DataZoomComponent,
    GraphicComponent,
    PieChart,
    LineChart,
    MarkLineComponent,
]);

setupConsoleInterceptor(); // console logs factory resolver.
// --- Bootstrap Standalone Application ---
bootstrapApplication(AppComponent, {
    providers: [
        provideNativeDateAdapter(),
        provideCustomDateAdapters(),
        provideNgxMatDateAdapter(
            CustomAdapter, CUSTOM_DATE_TIME_FORMATS
        ),
        // --- Angular Core & Common Providers ---
        provideAnimations(),
        provideHttpClient(withInterceptorsFromDi()), // Provides HttpClient and allows existing Class-based interceptors via DI

        // --- Configure Router ---
        provideRouter(appRoutes, withPreloading(PreloadAllModules)),
        // --- Import Providers from NgModules ---
        importProvidersFrom(
            ToastrModule.forRoot({
                maxOpened: 3,
                timeOut: 3000,
                positionClass: "toast-bottom-left",
                preventDuplicates: true,
                closeButton: true,
            }),
            QuillModule.forRoot(), // Quill configuration
            MatDialogModule,
            SharedModule,
            NgxEchartsModule.forRoot({ echarts }), // Echarts configuration
            NgxSkeletonLoaderModule.forRoot({
                // Skeleton Loader configuration
                animation: "progress",
                loadingText: "This item is actually loading...",
            }),
        ),

        // --- Application-Specific Providers (Interceptors, Config, Initializers, Error Handling) ---
        { provide: HTTP_INTERCEPTORS, useClass: TimeoutInterceptor, multi: true },
        { provide: DEFAULT_TIMEOUT, useValue: 90000 },
        { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
        { provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { appearance: "outline" } },
        {
            provide: APP_INITIALIZER,
            useFactory: visibilityChangeListenerFactory,
            deps: [ViewTaskSelectionHandlingService],
            multi: true,
        },
        {
            provide: APP_INITIALIZER,
            useFactory: (coreInit: CoreInitializationService) => () => coreInit.initialize(),
            deps: [CoreInitializationService],
            multi: true,
        },
        { provide: ErrorHandler, useClass: GlobalErrorHandler },
    ],
}).catch((err) => console.error(err));
